
import React, { Component } from 'react';

import { Dialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import { getParticipantById, getParticipantDisplayName, participantVolumeChanged } from '../../../base/participants';
import { connect } from '../../../base/redux';

/**
 * Used to modify initialValue, which is expected to be a decimal value between
 * 0 and 1, and converts it to a number representable by an input slider, which
 * recognizes whole numbers.
 */
const VOLUME_SLIDER_SCALE = 100;

/**
 * The type of the React {@code Component} props of {@link VolumeControlDialog}.
 */
type Props = {

    /**
     * The participant ID for which the volume control is being shown.
     */
    participantID: string,

    /**
     * The initial volume value (0-1).
     */
    initialVolumeValue: number,

    /**
     * Callback function to handle volume changes.
     */
    onVolumeChange: Function,

    /**
     * The display name of the participant.
     */
    participantName: string,

    /**
     * The translate function.
     */
    t: Function
};

/**
 * The type of the React {@code Component} state of {@link VolumeControlDialog}.
 */
type State = {

    /**
     * The current volume level.
     */
    volumeLevel: number
};

/**
 * A React Component with the contents for a dialog that allows controlling
 * the volume of a remote participant.
 *
 * @augments Component
 */
class VolumeControlDialog extends Component<Props, State> {
    /**
     * Initializes a new {@code VolumeControlDialog} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            volumeLevel: this.props.initialVolumeValue
        };

        // Bind event handlers so they are only bound once for every instance.
        this._onSubmit = this._onSubmit.bind(this);
        this._onVolumeChange = this._onVolumeChange.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidUpdate()}.
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        // Update the volume level if the initialValue prop changes
        if (prevProps.initialVolumeValue !== this.props.initialVolumeValue) {
            this.setState({
                volumeLevel: this.props.initialVolumeValue || 0
            });
        }
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { participantName, t } = this.props;

        const style1 = { padding: '20px 0' };
        const style2 = { marginBottom: '20px',
            textAlign: 'center' };
        const style3 = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
        };
        const style4 = { minWidth: '60px',
            fontSize: '14px' };
        const style5 = { flex: 1,
            maxWidth: '200px' };
        const style6 = { width: '100%' };
        const style7 = {
            minWidth: '40px',
            fontSize: '14px',
            fontWeight: 'bold'
        };

        return (
            <Dialog
                okKey = { t('dialog.apply') }
                onSubmit = { this._onSubmit }
                titleKey = 'volumeSlider.label'
                width = 'small'>
                <div style = { style1 }>
                    <p
                        style = { style2 }>
                        {t('volumeSlider.adjustVolumeFor', { participantName })}
                    </p>
                    <div
                        style = { style3 }>
                        <span
                            style = { style4 }>
                            {t('volumeSlider.volume')}:
                        </span>
                        <div
                            style = { style5 }>
                            <input
                                max = { VOLUME_SLIDER_SCALE }
                                min = { 0 }
                                onChange = { this._onVolumeChange }
                                step = { 1 }
                                style = { style6 }
                                type = 'range'
                                value = { this.state.volumeLevel } />
                        </div>
                        <span
                            style = { style7 }>
                            {Math.round(this.state.volumeLevel)}%
                        </span>
                    </div>
                </div>
            </Dialog>
        );
    }

    _onSubmit: () => boolean;

    /**
     * Handles the submit action of the dialog.
     * Since volume changes are applied in real-time, we just close the dialog.
     *
     * @private
     * @returns {boolean} - True to close the dialog.
     */
    _onSubmit() {
        // Volume changes are already applied in real-time via _onVolumeChange
        return true;
    }

    _onVolumeChange: (Object) => void;

    /**
     * Handles volume slider changes. Updates the volume in real-time
     * like the VolumeSlider component.
     *
     * @param {Object} event - The change event.
     * @private
     * @returns {void}
     */
    _onVolumeChange(event) {
        const volumeLevel = event.currentTarget.value;
        const { onVolumeChange } = this.props;

        // Call onVolumeChange immediately for real-time synchronization
        if (onVolumeChange) {
            onVolumeChange(volumeLevel / VOLUME_SLIDER_SCALE);
        }

        this.setState({ volumeLevel });
        APP.store.dispatch(
            participantVolumeChanged(this.props.participantID, volumeLevel));
    }
}

/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Props} ownProps - The own props of the component.
 * @returns {Props}
 */
function mapStateToProps(state, ownProps) {
    const { participantID } = ownProps;

    return {
        participantName: getParticipantDisplayName(state, participantID),
        participant: getParticipantById(state, participantID),
        initialVolumeValue: getParticipantById(state, participantID)?.volume || 100
    };
}

export default translate(connect(mapStateToProps)(VolumeControlDialog));
